import type { CmsAdapter, CmsSource } from '@eneco/cms-adapter';
import env from '@eneco/common/env';
import { trimTrailingSlashes } from '@eneco/common/path';

import { createSanityAdapter } from './adapter';
import { isSanityEnabled, shouldUseSanityAdapter } from './is-sanity-enabled';
import { createSanityClientRunner } from './sanity-client-runner';

type SanityAdapter = ReturnType<typeof createSanityAdapter>;

/** Comma-separated env value to a trimmed list, or `undefined` when unset. */
const parseList = (value: string): string[] | undefined =>
  value
    ? value
        .split(',')
        .map(entry => entry.trim())
        .filter(Boolean)
    : undefined;

export interface CmsProviderAdapterOptions {
  /** `site` document scoping every page lookup, from the container's `config.ts`. */
  site?: string;
  /** Other sites the container serves under their own path prefix. See `SanityAdapterConfig.prefixedSites`. */
  prefixedSites?: readonly string[];
  /**
   * Per-container opt-in to allow the `?_cms=` override to activate on
   * acceptance/production, not just development/local. See
   * `is-sanity-enabled.ts`. Default `false` — every container except
   * `gb-eneco-main` stays dev/local-only.
   */
  allowSharedEnvironment?: boolean;
  /**
   * The container's Next base path, stripped before slug resolution. Pass
   * `getContainerBasePath()` from `@eneco/next/server/utils/getContainerBasePath`
   * (the deep path, not the barrel, which is Node-only). See
   * `SanityAdapterConfig.basePath` for why Sanity must not see it.
   *
   * `SANITY_BASE_PATH` overrides it, the counterpart to `SANITY_SITE`. Both are
   * needed together to point a local run at content that is not this
   * container's own.
   */
  basePath?: string;
  /**
   * Absolute Sanity Studio URL. `SANITY_STUDIO_URL` overrides it, the same way
   * `SANITY_SITE` and `SANITY_BASE_PATH` override their counterparts. Only used in
   * draft mode; see `SanityAdapterConfig.studioUrl`.
   */
  studioUrl?: string;
  /** Studio workspace name. `SANITY_STUDIO_WORKSPACE` overrides it; defaults to the dataset. */
  studioWorkspace?: string;
}

/**
 * Builds the provider-selecting adapter for a container's CMS-agnostic rendering lane
 * (`?_cms=sitecore|sanity`, dev/local by default — acceptance/production too for a
 * container that opts in via `allowSharedEnvironment`) — see `docs/cms-component-rendering.md`.
 *
 * Every container wires this identically; the only thing that differs between
 * them is how they build their own Sitecore adapter, so that's the one
 * parameter. This is what makes the two CMSs comparable: **both** providers
 * resolve through the same `CmsRouteContainer` + `cmsComponents` registry +
 * `CmsLayout` (`@eneco/cms-components`), so a difference on screen is a
 * difference in the *content pipeline*, not in the rendering path.
 *
 * Sitecore is gated on the same environment allowlist as Sanity here, so
 * neither lane can activate on a shared or customer-facing environment —
 * `?_cms=sitecore` deliberately does NOT reach the legacy
 * `SitecoreRouteContainer` lane, which uses a separate `sitecoreComponents`
 * registry and would tell us nothing about parity.
 */
export const createCmsProviderAdapter = (
  getSitecoreAdapter: () => CmsAdapter,
  options: CmsProviderAdapterOptions = {},
) => {
  let sanityInstance: SanityAdapter | undefined;
  let sanityResolved = false;

  /**
   * Returns the Sanity adapter only when the environment is enabled AND
   * `SANITY_*` config is present — otherwise `undefined`, so the container
   * stays on Sitecore. Safe on acceptance/prod: the gate is default-deny.
   */
  const getSanityAdapter = (): SanityAdapter | undefined => {
    if (!sanityResolved) {
      sanityResolved = true;
      const projectId = env('SANITY_PROJECT_ID');
      const dataset = env('SANITY_DATASET');
      // Env first, so a local run can point at another site without touching code.
      const site = env('SANITY_SITE') || options.site || undefined;
      // Same escape hatch for the other half of the lookup. A container serving
      // content that is not its own needs both: the site to scope to, and the URL
      // prefix to strip before slug resolution. Setting only `SANITY_SITE` leaves
      // every path prefixed and nothing matches.
      const basePath = env('SANITY_BASE_PATH') || options.basePath || undefined;
      // Absolute Studio URL, for stega encoding and click-to-edit navigation. Read at
      // runtime and carried to the browser on `CmsContext.editing` — deliberately not a
      // `NEXT_PUBLIC_` variable, which a build-once-run-everywhere container would bake
      // in as an empty string (`config/next/config.js`). Unset simply means draft mode
      // renders unpublished content without overlays.
      // Trailing slash stripped here, once, rather than at each use: `createEditUrl` (the
      // `@sanity/client` CSM helper behind `createDataAttribute`) *throws* `baseUrl must not end
      // with a slash`, and `stega.studioUrl` needs the workspace appended to it. Every container
      // sets `FE_SANITY_STUDIO_URL` with a trailing slash today, so this is the live value, not a
      // hypothetical one.
      const studioUrl = trimTrailingSlashes(env('SANITY_STUDIO_URL') || options.studioUrl || '') || undefined;
      // Defaults to the dataset below: Eneco's Studio names each workspace after the dataset it
      // binds (`development`, `acceptance`), so no extra container config is needed today.
      const studioWorkspace = env('SANITY_STUDIO_WORKSPACE') || options.studioWorkspace || undefined;
      if (
        shouldUseSanityAdapter({
          environment: env('ENVIRONMENT'),
          projectId,
          dataset,
          allowSharedEnvironment: options.allowSharedEnvironment,
        })
      ) {
        sanityInstance = createSanityAdapter({
          projectId,
          dataset,
          // `site` supersedes the type list rather than narrowing alongside it: the list has to be
          // kept in step with a 189-type bundle by hand, and a stale entry silently renders a blank
          // page. Ignoring it when a site is known is what lets a container work with no `.env` edit.
          documentType: site ? undefined : parseList(env('SANITY_DOCUMENT_TYPES')),
          site,
          // Only alongside the container's own site: a `SANITY_SITE` naming another points the lookup elsewhere.
          prefixedSites: site === options.site ? options.prefixedSites : undefined,
          basePath,
          // Only the page document's own top-level content field (e.g. `sections`) needs listing here —
          // GROQ needs that one name to select the field at all (`queries.ts#buildPageQuery`). A nested
          // slot field on a block (`Section.sectionMain`, `SplitView.splitViewFirst`, …) needs no entry:
          // `adapter.ts#toRendering` recognizes those from their content shape, not from this list.
          slotFields: parseList(env('SANITY_SLOT_FIELDS')),
          // Server-only @sanity/client transport (handles POST for long queries,
          // retries, CDN routing). The SDK is dynamically imported, so it stays
          // out of the browser bundle and the token never reaches the client.
          studioUrl,
          studioWorkspace,
          // The environment's own origin, which a link to another site's page is absolute on, as
          // Sitecore's are: `https://www.acc.eneco.nl/grootzakelijk/...` on acceptance.
          host: env('HOST') || undefined,
          queryRunner: createSanityClientRunner({
            projectId,
            dataset,
            token: env('SANITY_API_TOKEN') || undefined,
            studioUrl,
            studioWorkspace,
          }),
        });
      }
    }
    return sanityInstance;
  };

  const selectCmsAdapter = (provider: CmsSource): CmsAdapter | undefined => {
    if (!isSanityEnabled(env('ENVIRONMENT'), options.allowSharedEnvironment)) return undefined;
    return provider === 'sanity' ? getSanityAdapter() : getSitecoreAdapter();
  };

  return { getSanityAdapter, selectCmsAdapter };
};
